import React, { useState, useEffect, useCallback } from 'react';
import { tradeLicenseApi } from '../../../services/tradeLicenseApi';
import ListPagination from '../../../components/Office/ListPagination';
import './InspectionsList.css';

const PAGE_SIZE = 20;

const InspectionsList = () => {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('scheduled');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ count: 0, next: null, previous: null });
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [notes, setNotes] = useState('');
  const [recommendation, setRecommendation] = useState('approve');
  const [isCompliant, setIsCompliant] = useState('yes');
  const [error, setError] = useState(null);

  const fetchInspections = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        ...(filter ? { status: filter } : {}),
        page,
        page_size: PAGE_SIZE,
      };
      const response = await tradeLicenseApi.getInspections(params);
      const data = response.data;
      setInspections(data.results || data);
      setPagination({
        count: data.count ?? (Array.isArray(data) ? data.length : 0),
        next: data.next ?? null,
        previous: data.previous ?? null,
      });
    } catch (err) {
      console.error('Failed to fetch inspections:', err);
      setInspections([]);
      setPagination({ count: 0, next: null, previous: null });
    } finally {
      setLoading(false);
    }
  }, [filter, page]);

  useEffect(() => {
    fetchInspections();
  }, [fetchInspections]);

  const openInspection = (inspection) => {
    setSelectedInspection(inspection);
    setNotes('');
    setRecommendation('approve');
    setIsCompliant('yes');
    setError(null);
  };

  const closeModal = () => {
    setSelectedInspection(null);
    setError(null);
  };

  const handleCompleteInspection = async () => {
    if (!selectedInspection) return;
    if (!notes.trim()) {
      setError('Please enter inspection notes/comments.');
      return;
    }

    setActionLoading(true);
    setError(null);
    try {
      await tradeLicenseApi.completeInspection(selectedInspection.id, {
        recommendation,
        notes_en: notes,
        is_compliant: isCompliant === 'yes',
      });
      closeModal();
      fetchInspections();
    } catch (err) {
      const msg = err.response?.data?.error
        || Object.values(err.response?.data || {}).flat().join(' ')
        || err.message;
      setError(msg);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="inspections-container">
      <h1 className="page-title">Inspections Management</h1>

      <div className="filter-bar">
        <button
          className={`btn ${!filter ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => { setFilter(''); setPage(1); }}
        >
          All
        </button>
        <button
          className={`btn ${filter === 'scheduled' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => { setFilter('scheduled'); setPage(1); }}
        >
          Scheduled
        </button>
        <button
          className={`btn ${filter === 'in_progress' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => { setFilter('in_progress'); setPage(1); }}
        >
          In Progress
        </button>
        <button
          className={`btn ${filter === 'completed' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => { setFilter('completed'); setPage(1); }}
        >
          Completed
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : inspections.length > 0 ? (
        <>
        <div className="inspections-grid">
          {inspections.map((inspection) => (
            <div key={inspection.id} className="inspection-card">
              <div className="inspection-header">
                <h3>Inspection #{inspection.inspection_number}</h3>
                <span className={`status-badge ${inspection.status}`}>
                  {inspection.status.replace(/_/g, ' ').toUpperCase()}
                </span>
              </div>

              <dl className="inspection-details">
                <dt>Application:</dt>
                <dd>{inspection.application_number}</dd>

                <dt>Inspector:</dt>
                <dd>{inspection.assigned_to_name || 'Unassigned'}</dd>

                <dt>Scheduled Date:</dt>
                <dd>
                  {inspection.scheduled_date
                    ? new Date(inspection.scheduled_date).toLocaleString()
                    : '—'}
                </dd>
              </dl>

              <button
                className="btn btn-primary"
                onClick={() => openInspection(inspection)}
              >
                {inspection.status === 'completed' ? 'View Report' : 'Complete Inspection'}
              </button>
            </div>
          ))}
        </div>
        <ListPagination
          count={pagination.count}
          next={pagination.next}
          previous={pagination.previous}
          currentPage={page}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          loading={loading}
        />
        </>
      ) : (
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <p>No inspections found</p>
        </div>
      )}

      {selectedInspection && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content inspection-modal" onClick={(e) => e.stopPropagation()}>
            <h2>
              {selectedInspection.status === 'completed'
                ? 'Inspection Report'
                : 'Complete Inspection'}
            </h2>

            <dl className="detail-list">
              <dt>Inspection #:</dt>
              <dd>{selectedInspection.inspection_number}</dd>
              <dt>Application:</dt>
              <dd>{selectedInspection.application_number}</dd>
              <dt>Assigned To:</dt>
              <dd>{selectedInspection.assigned_to_name || 'Unassigned'}</dd>
              <dt>Scheduled:</dt>
              <dd>
                {selectedInspection.scheduled_date
                  ? new Date(selectedInspection.scheduled_date).toLocaleString()
                  : '—'}
              </dd>
            </dl>

            {selectedInspection.status === 'completed' ? (
              <>
                <dl className="detail-list">
                  <dt>Recommendation:</dt>
                  <dd>{selectedInspection.recommendation}</dd>
                  <dt>Compliant:</dt>
                  <dd>{selectedInspection.is_compliant ? 'Yes' : 'No'}</dd>
                  <dt>Notes:</dt>
                  <dd>{selectedInspection.inspection_notes_en || '—'}</dd>
                </dl>
                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={closeModal}>Close</button>
                </div>
              </>
            ) : (
              <>
                {error && <div className="alert alert-error">{error}</div>}

                <div className="form-group">
                  <label>Site Compliant? <span className="req">*</span></label>
                  <select
                    value={isCompliant}
                    onChange={(e) => setIsCompliant(e.target.value)}
                    className="modal-select"
                  >
                    <option value="yes">Yes — Compliant</option>
                    <option value="no">No — Non-compliant</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Recommendation <span className="req">*</span></label>
                  <select
                    value={recommendation}
                    onChange={(e) => setRecommendation(e.target.value)}
                    className="modal-select"
                  >
                    <option value="approve">Recommend Approval</option>
                    <option value="reject">Recommend Rejection</option>
                    <option value="reinspect">Requires Re-inspection</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Inspection Notes / Comments <span className="req">*</span></label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows="4"
                    className="modal-textarea"
                    placeholder="Describe findings, issues observed, compliance status…"
                  />
                </div>

                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={closeModal} disabled={actionLoading}>
                    Cancel
                  </button>
                  <button
                    className={`btn ${recommendation === 'reject' ? 'btn-danger' : 'btn-primary'}`}
                    onClick={handleCompleteInspection}
                    disabled={actionLoading || !notes.trim()}
                  >
                    {actionLoading ? 'Submitting…' : 'Submit Inspection Report'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InspectionsList;
